import { useCallback, useEffect, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth } from "@/lib/utils";
import i18n from "@/lib/i18n";

export type GoogleConnectionLevel = "user" | "organization";
export type GoogleService = "drive" | "gmail";

export interface GoogleConnection {
  id: string;
  connection_level: GoogleConnectionLevel;
  owner_workos_user_id: string | null;
  google_sub: string;
  google_email: string | null;
  account_label: string | null;
  status: "active" | "reauth_required" | "consent_missing" | "revoked" | string;
  requested_scopes: string[];
  granted_scopes: string[];
  allowed_scopes: string[];
  services: GoogleService[];
  created_by_workos_user_id: string;
  created_at: string;
  updated_at: string;
  last_refreshed_at: string | null;
  default_for?: GoogleService[];
}

export interface GoogleConnectionsData {
  personal_connections: GoogleConnection[];
  organization_connections: GoogleConnection[];
}

export interface UseGoogleConnectionsReturn {
  data: GoogleConnectionsData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  connect: (
    level: GoogleConnectionLevel,
    scopes: string[],
    target_connection_id?: string,
  ) => Promise<string | null>;
  updateConnection: (
    id: string,
    values: { account_label?: string | null; allowed_scopes?: string[] },
  ) => Promise<GoogleConnection | null>;
  setDefault: (service: GoogleService, connection_id: string) => Promise<boolean>;
  disconnect: (id: string) => Promise<boolean>;
  reconnect: (id: string, scopes?: string[]) => Promise<string | null>;
}

async function errorMessage(response: Response): Promise<string> {
  const body = await response.json().catch(() => ({}));
  return body.error || body.detail || `HTTP ${response.status}`;
}

export function useGoogleConnections(getToken: () => Promise<string>): UseGoogleConnectionsReturn {
  const [data, setData] = useState<GoogleConnectionsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth("/api/google/connections", { headers: {} }, getToken);
      if (!response.ok) throw new Error(await errorMessage(response));
      setData(await response.json() as GoogleConnectionsData);
    } catch (err: any) {
      setError(err?.message ?? i18n.t("connections:google.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const connect = useCallback(
    async (
      level: GoogleConnectionLevel,
      scopes: string[],
      target_connection_id?: string,
    ): Promise<string | null> => {
      try {
        const response = await fetchWithAuth(
          "/api/google/auth-url",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ connection_level: level, scopes, target_connection_id }),
          },
          getToken,
        );
        if (!response.ok) throw new Error(await errorMessage(response));
        const body = await response.json();
        return typeof body.url === "string" ? body.url : null;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("connections:google.errors.authorize"));
        return null;
      }
    },
    [getToken],
  );

  const updateConnection = useCallback(
    async (
      id: string,
      values: { account_label?: string | null; allowed_scopes?: string[] },
    ): Promise<GoogleConnection | null> => {
      try {
        const response = await fetchWithAuth(
          `/api/google/connections/${encodeURIComponent(id)}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(values),
          },
          getToken,
        );
        if (!response.ok) throw new Error(await errorMessage(response));
        const connection = await response.json() as GoogleConnection;
        await refresh();
        return connection;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("connections:google.errors.update"));
        return null;
      }
    },
    [getToken, refresh],
  );

  const setDefault = useCallback(
    async (service: GoogleService, connection_id: string): Promise<boolean> => {
      try {
        const response = await fetchWithAuth(
          `/api/google/defaults/${service}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ connection_id }),
          },
          getToken,
        );
        if (!response.ok) throw new Error(await errorMessage(response));
        await refresh();
        return true;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("connections:google.errors.setDefault"));
        return false;
      }
    },
    [getToken, refresh],
  );

  const disconnect = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const response = await fetchWithAuth(
          `/api/google/connections/${encodeURIComponent(id)}`,
          { method: "DELETE", headers: {} },
          getToken,
        );
        if (!response.ok) throw new Error(await errorMessage(response));
        await refresh();
        return true;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("connections:google.errors.disconnect"));
        return false;
      }
    },
    [getToken, refresh],
  );

  const reconnect = useCallback(
    async (id: string, scopes?: string[]): Promise<string | null> => {
      try {
        const response = await fetchWithAuth(
          `/api/google/connections/${encodeURIComponent(id)}/reconnect`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(scopes ? { scopes } : {}),
          },
          getToken,
        );
        if (!response.ok) throw new Error(await errorMessage(response));
        const body = await response.json();
        return typeof body.url === "string" ? body.url : null;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("connections:google.errors.reconnect"));
        return null;
      }
    },
    [getToken],
  );

  useRefreshOnReconnect(refresh);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    data,
    loading,
    error,
    refresh,
    connect,
    updateConnection,
    setDefault,
    disconnect,
    reconnect,
  };
}
